import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CompanyMark } from "../../Chat/CompanyMark";
import { hapticSelection } from "../../../lib/haptics";
import { focusRingSx, panelBorder, panelSurface } from "../overviewPanel";
import { formatNumber } from "../Charts/utils/formatNumber";
import { getDisplayCurrencySymbol } from "../../../utils/stockPriceFormatter";
import { EarningsCalendarEvent } from "../../../hooks/useEarningsCalendar";
import { ROW_HEIGHT, isTimeConfirmed } from "./earningsCalendarSelection";

// One reporting company — shared by the week grid and the day modal so the two
// always read identically.
//
// Sized to be scanned, not read: the ticker and the market cap share the top
// line because they are the two things a name is recognised by, and the legal
// name is a quiet second line that truncates rather than wrapping. The height
// is fixed so every slot on the board is the same — "Chagee Holdings Limited
// American Depositary Shares" never gets a say in it. No tooltip on the full
// name: a hover bubble on every card gets in the way of scanning, and the
// company page is one tap away. Deliberately no price and no estimates — this
// surface answers "who reports, and when".

// The cap is stored in the listing's own currency, in major units, so only the
// symbol needs choosing.
const capLabel = (event: EarningsCalendarEvent): string | null => {
  if (event.marketCap == null) return null;
  return `${getDisplayCurrencySymbol(event.currency)}${formatNumber(event.marketCap)}`;
};

const EarningsCompanyRow: React.FC<{ event: EarningsCalendarEvent }> = ({
  event,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const cap = capLabel(event);
  const confirmed = isTimeConfirmed(event);

  const open = () => {
    hapticSelection();
    navigate(`/earnings/company/${event.symbol}`);
  };

  return (
    <Box
      component="button"
      onClick={open}
      aria-label={`${event.companyName} (${event.symbol}) earnings${confirmed ? "" : ", time to be confirmed"}`}
      sx={{
        all: "unset",
        ...focusRingSx,
        boxSizing: "border-box",
        width: "100%",
        height: ROW_HEIGHT,
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        minWidth: 0,
        cursor: "pointer",
        fontFamily: "inherit",
        borderRadius: 1.25,
        backgroundColor: panelSurface(isDark),
        border: `1px solid ${panelBorder(isDark)}`,
        WebkitTapHighlightColor: "transparent",
        transition:
          "background-color 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
        "&:hover": {
          backgroundColor: isDark
            ? "rgba(34, 197, 94, 0.05)"
            : "rgba(34, 197, 94, 0.04)",
          borderColor: "rgba(34, 197, 94, 0.45)",
          "& .earnings-ticker": { color: "#22c55e" },
        },
        "&:active": { transform: "scale(0.98)" },
      }}
    >
      <CompanyMark symbol={event.symbol} size={28} radius="6px" />
      {/* minWidth:0 on the text column is what lets the name ellipsis —
          without it the flex child refuses to shrink below its content. */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Typography
            className="earnings-ticker"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.8125rem",
              fontWeight: 700,
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              color: theme.palette.text.primary,
              transition: "color 0.2s ease",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.symbol}
          </Typography>
          {cap && (
            <Typography
              sx={{
                flexShrink: 0,
                fontSize: "0.6875rem",
                fontWeight: 600,
                lineHeight: 1.3,
                color: theme.palette.text.secondary,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {cap}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.6875rem",
              lineHeight: 1.35,
              color: theme.palette.text.secondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.companyName}
          </Typography>
          {!confirmed && (
            <Typography
              sx={{
                flexShrink: 0,
                fontSize: "0.6875rem",
                fontWeight: 700,
                lineHeight: 1.35,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: theme.palette.text.secondary,
              }}
            >
              TBC
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(EarningsCompanyRow);
